import { useApolloClient, useMutation } from '@apollo/client';
import { Button, Input } from '@newfront-insurance/core-ui/v2';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';

import { findOrganizationNonMembers, organizationNonMembersMessage } from './check-organization-membership';
import { getNovaGroupError, INLINE_ERROR_MUTATION_OPTIONS } from './group-mutation-errors';
import { TeamOrganizationField } from './team-organization-field';
import { useOrganizationPeopleEligibility } from './use-organization-people-eligibility';
import { CREATE_SETTINGS_TEAM } from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useUnsavedNovaChanges } from '../../navigation/use-unsaved-nova-changes';
import type { SettingsPanelCloseGuard } from '../settings-panel-context';
import type { SettingsEmployee } from '../settings-people/employee-utils';
import { SettingsPeoplePicker } from '../settings-people/people-picker';

export function TeamCreateView({
  userUuid,
  onBack,
  cancelRef,
}: {
  userUuid: string;
  onBack: () => void;
  cancelRef?: MutableRefObject<SettingsPanelCloseGuard | null>;
}) {
  const { settingsAdministration, userUuid: currentUserUuid } = useChatOverlayContext();
  const { organizations, status } = settingsAdministration;
  const [organizationUuid, setOrganizationUuid] = useState<string | null>(() =>
    organizations.length === 1 ? organizations[0].uuid : null,
  );
  const client = useApolloClient();
  const notify = useNotify();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [employees, setEmployees] = useState<SettingsEmployee[]>([]);
  const [saveError, setSaveError] = useState<string>();
  const [nameError, setNameError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [createTeam] = useMutation(CREATE_SETTINGS_TEAM, INLINE_ERROR_MUTATION_OPTIONS);
  const submitting = useRef(false);
  const active = useRef(true);
  const excludedUserUuids = useMemo(() => new Set([userUuid]), [userUuid]);
  const organization = organizations.find((root) => root.uuid === organizationUuid) ?? null;
  const hasOrganizationAccess = userUuid === currentUserUuid && status === 'ready' && !!organization;
  const eligibility = useOrganizationPeopleEligibility(organizationUuid, hasOrganizationAccess);
  const tooManyEmployees = employees.length > 100;
  const canSave = hasOrganizationAccess && !!name.trim() && !tooManyEmployees && !isSaving;
  const accessRef = useRef(hasOrganizationAccess);
  useUnsavedNovaChanges(Boolean(name.trim() || description.trim()) || employees.length > 0 || isSaving);

  useEffect(() => {
    accessRef.current = hasOrganizationAccess;
  }, [hasOrganizationAccess]);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  // Settings navigation uses the same close guard as skill creation. Keep a pending save in its form.
  useEffect(() => {
    if (!cancelRef) return;
    cancelRef.current = () => !submitting.current;
    return () => {
      cancelRef.current = null;
    };
  }, [cancelRef]);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave || !organizationUuid || submitting.current) return;
    submitting.current = true;
    setIsSaving(true);
    setSaveError(undefined);
    setNameError(undefined);
    try {
      let nonMembers;
      try {
        nonMembers = await findOrganizationNonMembers(client, organizationUuid, [
          { userUuid, name: 'You', email: '' },
          ...employees,
        ]);
      } catch {
        if (active.current && accessRef.current)
          setSaveError("Couldn't verify organization membership. Your entries are kept. Try saving again.");
        return;
      }
      if (!active.current || !accessRef.current) return;
      if (nonMembers.length) {
        setSaveError(organizationNonMembersMessage(nonMembers));
        return;
      }
      const { data } = await createTeam({
        variables: {
          name: name.trim(),
          description: description.trim() || null,
          parentGroupUuid: organizationUuid,
          memberUserUuids: employees.map((employee) => employee.userUuid),
        },
      });
      if (!data?.createSkillGroup.uuid || data.createSkillGroup.parentGroupUuid !== organizationUuid) {
        throw new Error('Missing created team');
      }
      if (!active.current || !accessRef.current) return;
      settingsAdministration.retry();
      notify.success('Team created.');
      // The shared navigation action checks the guard again before leaving.
      submitting.current = false;
      onBack();
    } catch (error) {
      if (!active.current || !accessRef.current) return;
      const groupError = getNovaGroupError(error);
      if (groupError?.code === 'NAME_ALREADY_EXISTS') setNameError(groupError.message);
      else if (groupError?.code === 'USER_NOT_IN_GROUP') setSaveError(groupError.message);
      else setSaveError("Couldn't create this team. Your entries are kept. Try saving again.");
    } finally {
      submitting.current = false;
      if (active.current) setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={(event) => void save(event)}
      aria-label="New team"
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-white"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-10">
        <div className="mx-auto flex w-full max-w-[736px] flex-col gap-6">
          <h2 className="text-steel-500 text-lg font-normal">New team</h2>
          <TeamOrganizationField
            organizations={organizations}
            organization={organization}
            disabled={isSaving || status !== 'ready' || userUuid !== currentUserUuid}
            onChange={(uuid) => {
              if (submitting.current || uuid === organizationUuid) return;
              setOrganizationUuid(uuid);
              setEmployees([]);
              setSaveError(undefined);
            }}
          />
          <div className="flex flex-col gap-2">
            <label htmlFor="settings-team-name" className="text-steel-500 text-base font-semibold">
              Team name
            </label>
            <Input
              fieldId="settings-team-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(undefined);
              }}
              placeholder="e.g. Account managers"
              required
              disabled={isSaving}
              isInvalid={!!nameError}
              aria-describedby={nameError ? 'settings-team-name-error' : undefined}
              autoFocus
            />
            {nameError ? (
              <p id="settings-team-name-error" role="alert" className="text-fire-500 text-sm">
                {nameError}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="settings-team-description" className="text-steel-500 text-base font-semibold">
              Description
            </label>
            <Input
              fieldId="settings-team-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What is this team for?"
              disabled={isSaving}
            />
          </div>
          <div className="flex flex-col gap-2 pb-64">
            <label htmlFor="settings-team-people" className="text-steel-500 text-base font-semibold">
              Add teammates
            </label>
            <SettingsPeoplePicker
              key={organizationUuid ?? 'unselected'}
              inputId="settings-team-people"
              excludedUserUuids={excludedUserUuids}
              eligibility={eligibility}
              selectedEmployees={employees}
              disabled={isSaving || !hasOrganizationAccess}
              onAddEmployee={(employee) =>
                setEmployees((current) =>
                  current.some((item) => item.userUuid === employee.userUuid) ? current : [...current, employee],
                )
              }
              onRemoveEmployee={(uuid) =>
                setEmployees((current) => current.filter((employee) => employee.userUuid !== uuid))
              }
            />
            <p className="text-steel-400 text-sm">Teammates must already belong to the organization.</p>
          </div>
        </div>
      </div>
      <div className="border-steel-75 border-t px-4 py-4 sm:px-8">
        <div className="mx-auto flex w-full max-w-[736px] flex-col gap-3">
          {!hasOrganizationAccess &&
          (organizationUuid !== null || !organizations.length || userUuid !== currentUserUuid || status !== 'ready') ? (
            <p role="alert" className="text-steel-400 text-sm">
              Organization access is unavailable. Return to Teams and try again.
            </p>
          ) : tooManyEmployees ? (
            <p role="alert" className="text-fire-500 text-sm">
              Add up to 100 teammates when creating a team.
            </p>
          ) : saveError ? (
            <p role="alert" className="text-fire-500 text-sm">
              {saveError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2.5">
            <Button
              type="button"
              variant="secondary"
              className="min-w-24"
              disabled={isSaving}
              onClick={() => {
                if (!submitting.current) onBack();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-w-24" disabled={!canSave}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
